import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  FileUploadOutlined as ImportIcon,
  IosShareOutlined as ExportIcon,
  RefreshOutlined as RefreshIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';
import ExportOtpDialog from '../components/ExportOtpDialog';

async function downloadTextFile({ filename, content, mime = 'text/csv;charset=utf-8', extensions }) {
  try {
    if (typeof window !== 'undefined' && window.showSaveFilePicker) {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'File',
            accept: { [String(mime).split(';')[0]]: Array.isArray(extensions) ? extensions : ['.csv'] },
          },
        ],
      });
      const writable = await handle.createWritable();
      const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
      await writable.write(blob);
      await writable.close();
      return;
    }
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) return;
  }
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCsvValue(v) {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
}

function parseCsvText(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (ch === ',' && !inQuotes) {
        cells.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    cells.push(cur.trim());
    rows.push(cells);
  }
  return rows;
}

function normalizeHeaderCell(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function looksLikeExcelFile(file) {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

async function parseExcelFileToRows(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames?.[0];
  const ws = wb.Sheets?.[sheetName];
  if (!ws) return [];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  return Array.isArray(aoa) ? aoa : [];
}

export default function DailyHeadOfficeTransactionReport() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm = permissionsMap.head_office_report || {};
  const canUpdate = isSuperAdmin || Boolean(pagePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(pagePerm.can_delete);
  const canImport = isSuperAdmin || Boolean(pagePerm.can_import);
  const canExport = isSuperAdmin || Boolean(pagePerm.can_export);

  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('txn_date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterFromVendor, setFilterFromVendor] = useState('');
  const [filterToVendor, setFilterToVendor] = useState('');
  const [filterTxnType, setFilterTxnType] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [form, setForm] = useState({
    userCode: '',
    date: '',
    fromVendorCode: '',
    fromBankCode: '',
    toVendorCode: '',
    toBankCode: '',
    amountPaid: '',
    cashReceived: '',
    oldCashBalance: '',
    commissionPct: '',
    adjustments: '',
    txnType: '',
    siteContactPerson: '',
    rectification: '',
    purpose: '',
  });

  const loadRows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/office-transactions`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRows(items);
    } catch (e) {
      setToast({ open: true, message: 'Failed to load transactions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [user]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const copy = rows.slice();
    copy.sort((a, b) => {
      const av = a?.[orderBy];
      const bv = b?.[orderBy];
      if (av === bv) return 0;
      if (order === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
    return copy;
  }, [rows, orderBy, order]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sd = startDate ? new Date(startDate) : null;
    const ed = endDate ? new Date(endDate) : null;
    return sortedRows.filter((r) => {
      const dateStr = r.txn_date ? String(r.txn_date).slice(0, 10) : '';
      const d = dateStr ? new Date(dateStr) : null;
      if (sd && d && d < sd) return false;
      if (ed && d && d > ed) return false;
      if (filterUser && r.user_code !== filterUser) return false;
      if (filterFromVendor && r.from_vendor_code !== filterFromVendor) return false;
      if (filterToVendor && r.to_vendor_code !== filterToVendor) return false;
      if (filterTxnType && r.txn_type !== filterTxnType) return false;
      if (!q) return true;
      return [
        r.user_code,
        r.txn_date,
        r.from_vendor_code,
        r.from_bank_code,
        r.to_vendor_code,
        r.to_bank_code,
        r.txn_type,
        r.site_contact_person,
        r.rectification,
        r.purpose,
      ]
        .map((x) => String(x || '').toLowerCase())
        .some((s) => s.includes(q));
    });
  }, [sortedRows, query, startDate, endDate, filterUser, filterFromVendor, filterToVendor, filterTxnType]);

  const formatINR = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);

  const totalAmountPaid = filteredRows.reduce((sum, r) => sum + (Number(r.amount_paid) || 0), 0);
  const totalCashReceived = filteredRows.reduce((sum, r) => sum + (Number(r.cash_received) || 0), 0);
  const totalOldCashBalance = filteredRows.reduce((sum, r) => sum + (Number(r.old_cash_balance) || 0), 0);
  const totalAdjustments = filteredRows.reduce((sum, r) => sum + (Number(r.adjustments) || 0), 0);

  const StatBox = ({ title, value, valueColor, borderColor }) => (
    <Paper sx={{ p: 3, height: 120, borderRadius: 3, border: '2px solid', borderColor, width: '100%' }}>
      <Typography align="center" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography align="center" sx={{ mt: 1, fontWeight: 700, fontSize: 24, color: valueColor }}>
        {value}
      </Typography>
    </Paper>
  );

  const uniqueOptions = useMemo(() => {
    const u = new Set();
    const fv = new Set();
    const tv = new Set();
    const tt = new Set();
    rows.forEach((r) => {
      if (r.user_code) u.add(r.user_code);
      if (r.from_vendor_code) fv.add(r.from_vendor_code);
      if (r.to_vendor_code) tv.add(r.to_vendor_code);
      if (r.txn_type) tt.add(r.txn_type);
    });
    return {
      users: Array.from(u).sort(),
      fromVendors: Array.from(fv).sort(),
      toVendors: Array.from(tv).sort(),
      txnTypes: Array.from(tt).sort(),
    };
  }, [rows]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleImportFile = async (file) => {
    if (!canImport) {
      setToast({
        open: true,
        message: 'You do not have import permission for this page',
        severity: 'error',
      });
      return;
    }
    if (!file) return;
    try {
      const parsed = looksLikeExcelFile(file)
        ? await parseExcelFileToRows(file)
        : parseCsvText(await file.text());
      if (parsed.length === 0) throw new Error('Empty file');

      const header = parsed[0].map(normalizeHeaderCell);
      const body = parsed.slice(1);

      const idxUser = header.findIndex(
        (h) => h === 'usercode' || h === 'user_code' || h === 'applicationusercode'
      );
      const idxDate = header.findIndex(
        (h) =>
          h === 'date' ||
          h === 'txndate' ||
          h === 'txn_date' ||
          h === 'transactiondate' ||
          h === 'dateoftransaction'
      );
      const idxFromVendor = header.findIndex(
        (h) =>
          h === 'fromvendorcode' ||
          h === 'from_vendor_code' ||
          h === 'fromcode' ||
          h === 'fromacvendorcode'
      );
      const idxFromBank = header.findIndex(
        (h) => h === 'frombankcode' || h === 'from_bank_code' || h === 'frombankacvendorbankcode'
      );
      const idxToVendor = header.findIndex(
        (h) => h === 'tovendorcode' || h === 'to_vendor_code' || h === 'tocode' || h === 'toacvendorcode'
      );
      const idxToBank = header.findIndex(
        (h) => h === 'tobankcode' || h === 'to_bank_code' || h === 'tobankacvendorbankcode'
      );
      const idxAmountPaid = header.findIndex((h) => h === 'amountpaid');
      const idxCashReceived = header.findIndex((h) => h === 'cashreceived' || h === 'cashamountreceived');
      const idxOldCashBalance = header.findIndex((h) => h === 'oldcashbalance');
      const idxCommissionPct = header.findIndex(
        (h) => h === 'commissionpct' || h === 'applicablecommissionpercent'
      );
      const idxAdjustments = header.findIndex((h) => h === 'adjustments');
      const idxTxnType = header.findIndex((h) => h === 'txntype' || h === 'transactiontype');
      const idxSiteContact = header.findIndex((h) => h === 'sitecontactperson');
      const idxRectification = header.findIndex((h) => h === 'rectification' || h === 'anyrectification');
      const idxPurpose = header.findIndex((h) => h === 'purpose' || h === 'purposeofpayment');

      if (idxUser === -1 || idxDate === -1) {
        throw new Error('CSV headers must include: User Code, Date');
      }

      const incoming = [];
      for (const row of body) {
        const userCode = String(row[idxUser] ?? '').trim();
        const date = String(row[idxDate] ?? '').trim();
        if (!userCode || !date) continue;
        const toVendorCode = idxToVendor >= 0 ? String(row[idxToVendor] ?? '').trim() : '';
        const num = (v) => {
          const n = Number(String(v ?? '').trim());
          return Number.isFinite(n) ? n : 0;
        };
        incoming.push({
          userCode,
          date,
          fromVendorCode: String(row[idxFromVendor] ?? '').trim(),
          fromBankCode: String(row[idxFromBank] ?? '').trim(),
          toVendorCode,
          toBankCode: String(row[idxToBank] ?? '').trim(),
          amountPaid: num(row[idxAmountPaid]),
          cashReceived: num(row[idxCashReceived]),
          oldCashBalance: num(row[idxOldCashBalance]),
          commissionPct: num(row[idxCommissionPct]),
          adjustments: num(row[idxAdjustments]),
          txnType: String(row[idxTxnType] ?? '').trim(),
          siteContactPerson: String(row[idxSiteContact] ?? '').trim(),
          rectification: String(row[idxRectification] ?? '').trim(),
          purpose: String(row[idxPurpose] ?? '').trim(),
        });
      }

      if (incoming.length === 0) throw new Error('No valid rows found');

      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/office-transactions/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ items: incoming }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Import failed');
      setToast({
        open: true,
        message: `Imported ${data.inserted} rows, skipped ${data.skipped}`,
        severity: 'success',
      });
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: String(e?.message || e), severity: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(false);
    }
  };

  const performExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const data = rows.map((r) => ({
        'User Code': r.user_code,
        'Date': String(r.txn_date).slice(0, 10),
        'From Vendor Code': r.from_vendor_code,
        'From Bank Code': r.from_bank_code,
        'To Vendor Code': r.to_vendor_code,
        'To Bank Code': r.to_bank_code,
        'Amount Paid': r.amount_paid,
        'Cash Received': r.cash_received,
        'Old Cash Balance': r.old_cash_balance,
        'Commission %': r.commission_pct,
        'Adjustments': r.adjustments,
        'Transaction Type': r.txn_type,
        'Site Contact Person': r.site_contact_person,
        'Rectification': r.rectification,
        'Purpose': r.purpose,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'HeadOfficeTx');
      const arrayBuf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([arrayBuf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      await downloadTextFile({
        filename: 'daily_head_office_transactions.xlsx',
        content: blob,
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extensions: ['.xlsx'],
      });
    } catch (e) {
      setToast({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  const performExportCsv = async () => {
    const headers = [
      'User Code',
      'Date',
      'From Vendor Code',
      'From Bank Code',
      'To Vendor Code',
      'To Bank Code',
      'Amount Paid',
      'Cash Received',
      'Old Cash Balance',
      'Commission %',
      'Adjustments',
      'Transaction Type',
      'Site Contact Person',
      'Rectification',
      'Purpose',
    ];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const vals = [
        toCsvValue(r.user_code),
        toCsvValue(String(r.txn_date).slice(0, 10)),
        toCsvValue(r.from_vendor_code),
        toCsvValue(r.from_bank_code),
        toCsvValue(r.to_vendor_code),
        toCsvValue(r.to_bank_code),
        toCsvValue(r.amount_paid),
        toCsvValue(r.cash_received),
        toCsvValue(r.old_cash_balance),
        toCsvValue(r.commission_pct),
        toCsvValue(r.adjustments),
        toCsvValue(r.txn_type),
        toCsvValue(r.site_contact_person),
        toCsvValue(r.rectification),
        toCsvValue(r.purpose),
      ];
      lines.push(vals.join(','));
    }
    await downloadTextFile({
      filename: 'daily_head_office_transactions.csv',
      content: `${lines.join('\n')}\n`,
      mime: 'text/csv;charset=utf-8',
      extensions: ['.csv'],
    });
  };

  const onOtpSuccess = async () => {
    if (pendingExportType === 'excel') {
      await performExportExcel();
    } else if (pendingExportType === 'csv') {
      await performExportCsv();
    }
    setPendingExportType(null);
    setOtpDialogOpen(false);
  };

  const handleExportExcel = async () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have export permission for this page',
        severity: 'error',
      });
      return;
    }
    if (isSuperAdmin) {
      await performExportExcel();
    } else {
      setPendingExportType('excel');
      setOtpDialogOpen(true);
    }
  };

  const handleExportCsv = async () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have export permission for this page',
        severity: 'error',
      });
      return;
    }
    if (isSuperAdmin) {
      await performExportCsv();
    } else {
      setPendingExportType('csv');
      setOtpDialogOpen(true);
    }
  };

  const downloadTemplate = () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have export permission for this page',
        severity: 'error',
      });
      return;
    }
    const headers = [
      'User Code',
      'Date',
      'From Vendor Code',
      'From Bank Code',
      'To Code',
      'To Bank Code',
      'Amount Paid',
      'Cash Received',
      'Old Cash Balance',
      'Commission Pct',
      'Adjustments',
      'Txn Type',
      'Site Contact Person',
      'Rectification',
      'Purpose',
    ];
    downloadTextFile({ filename: 'head_office_tx_template.csv', content: `${headers.join(',')}\n` });
  };

  const openEdit = (row) => {
    if (!canUpdate) {
      setToast({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    setEditingId(row.id);
    setForm({
      userCode: row.user_code || '',
      date: String(row.txn_date || '').slice(0, 10),
      fromVendorCode: row.from_vendor_code || '',
      fromBankCode: row.from_bank_code || '',
      toVendorCode: row.to_vendor_code || '',
      toBankCode: row.to_bank_code || '',
      amountPaid: String(row.amount_paid ?? ''),
      cashReceived: String(row.cash_received ?? ''),
      oldCashBalance: String(row.old_cash_balance ?? ''),
      commissionPct: String(row.commission_pct ?? ''),
      adjustments: String(row.adjustments ?? ''),
      txnType: row.txn_type || '',
      siteContactPerson: row.site_contact_person || '',
      rectification: row.rectification || '',
      purpose: row.purpose || '',
    });
    setEditOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const submitEdit = async () => {
    if (!canUpdate) {
      setToast({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/office-transactions/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          userCode: form.userCode,
          date: form.date,
          fromVendorCode: form.fromVendorCode,
          fromBankCode: form.fromBankCode,
          toVendorCode: form.toVendorCode,
          toBankCode: form.toBankCode,
          amountPaid: Number(form.amountPaid),
          cashReceived: Number(form.cashReceived),
          oldCashBalance: Number(form.oldCashBalance),
          commissionPct: Number(form.commissionPct),
          adjustments: Number(form.adjustments),
          txnType: form.txnType,
          siteContactPerson: form.siteContactPerson,
          rectification: form.rectification,
          purpose: form.purpose,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Update failed');
      setToast({ open: true, message: 'Updated successfully', severity: 'success' });
      setEditOpen(false);
      setEditingId(null);
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: String(e?.message || e), severity: 'error' });
    }
  };

  const openDelete = (row) => {
    if (!canDelete) {
      setToast({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      return;
    }
    setEditingId(row.id);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!canDelete) {
      setToast({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      setDeleteOpen(false);
      setEditingId(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/office-transactions/${editingId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setToast({ open: true, message: 'Deleted successfully', severity: 'success' });
      setDeleteOpen(false);
      setEditingId(null);
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: String(e?.message || e), severity: 'error' });
    }
  };

  return (
    <Layout title="Daily Head Office Transactions">
      <Box sx={{ width: '100%' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 650, letterSpacing: -0.3 }}>
              Daily Head Office Transactions
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              View, import and export transactions (Excel/CSV).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadRows} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              startIcon={<ExportIcon />}
              variant="contained"
              color="success"
              onClick={handleExportExcel}
              disabled={loading || !canExport}
            >
              Export Excel
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              color="info"
              onClick={handleExportCsv}
              disabled={loading || !canExport}
            >
              Export CSV
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              color="secondary"
              onClick={downloadTemplate}
              disabled={loading || !canExport}
            >
              Template
            </Button>
            <Button
              startIcon={<ImportIcon />}
              variant="contained"
              onClick={() => fileInputRef.current?.click?.()}
              disabled={loading || !canImport}
            >
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              style={{ display: 'none' }}
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
          </Stack>
        </Stack>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, 
          gap: 3, 
          mb: 4, 
          width: '100%' 
        }}>
          <StatBox title="Total Transactions" value={filteredRows.length} valueColor="#2563eb" borderColor="#bfdbfe" />
          <StatBox title="Total Amount Paid" value={formatINR(totalAmountPaid)} valueColor="#dc2626" borderColor="#fecaca" />
          <StatBox title="Total Cash Received" value={formatINR(totalCashReceived)} valueColor="#16a34a" borderColor="#bbf7d0" />
          <StatBox title="Total Old Cash Balance" value={formatINR(totalOldCashBalance)} valueColor="#9333ea" borderColor="#e9d5ff" />
          <StatBox title="Total Adjustments" value={formatINR(totalAdjustments)} valueColor="#d97706" borderColor="#fde68a" />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            borderColor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search"
              sx={{ minWidth: 220, flex: '1 1 220px' }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            />
            <TextField
              label="User"
              select
              size="small"
              value={filterUser}
              onChange={(e) => {
                setFilterUser(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 140, flex: '1 1 140px' }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {uniqueOptions.users.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="From Vendor"
              select
              size="small"
              value={filterFromVendor}
              onChange={(e) => {
                setFilterFromVendor(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {uniqueOptions.fromVendors.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="To Vendor"
              select
              size="small"
              value={filterToVendor}
              onChange={(e) => {
                setFilterToVendor(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {uniqueOptions.toVendors.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Txn Type"
              select
              size="small"
              value={filterTxnType}
              onChange={(e) => {
                setFilterTxnType(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 140, flex: '1 1 140px' }}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {uniqueOptions.txnTypes.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            borderColor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eef2ff'),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ fontWeight: 750 }}>Transactions</Typography>
              <Chip
                size="small"
                label={
                  rows.length === filteredRows.length ? `${rows.length} total` : `${filteredRows.length} filtered`
                }
              />
            </Stack>
          </Box>

          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
                  color: (t) => (t.palette.mode === 'dark' ? 'common.white' : '#0f172a'),
                  fontWeight: 800,
                  py: 1.5,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'txn_date'}
                      direction={orderBy === 'txn_date' ? order : 'desc'}
                      onClick={() => handleSort('txn_date')}
                      sx={{ color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'user_code'}
                      direction={orderBy === 'user_code' ? order : 'asc'}
                      onClick={() => handleSort('user_code')}
                      sx={{ color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                    >
                      User Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>From Vendor Code</TableCell>
                  <TableCell>From Bank Code</TableCell>
                  <TableCell>To Vendor Code</TableCell>
                  <TableCell>To Bank Code</TableCell>
                  <TableCell align="right">Amount Paid</TableCell>
                  <TableCell align="right">Cash Received</TableCell>
                  <TableCell align="right">Old Cash Balance</TableCell>
                  <TableCell align="right">Commission %</TableCell>
                  <TableCell align="right">Adjustments</TableCell>
                  <TableCell>Txn Type</TableCell>
                  <TableCell>Site Contact Person</TableCell>
                  <TableCell>Rectification</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell align="center" sx={{ width: 140 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow
                    key={`${r.id}`}
                    hover
                    onClick={() => {
                      setViewRow(r);
                      setViewOpen(true);
                    }}
                    sx={{
                      '& td': { py: 1.25, fontSize: 14 },
                      '&:hover td': {
                        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'),
                      },
                    }}
                  >
                    <TableCell>{String(r.txn_date).slice(0, 10)}</TableCell>
                    <TableCell>{r.user_code}</TableCell>
                    <TableCell>{r.from_vendor_code}</TableCell>
                    <TableCell>{r.from_bank_code}</TableCell>
                    <TableCell>{r.to_vendor_code}</TableCell>
                    <TableCell>{r.to_bank_code}</TableCell>
                    <TableCell align="right">{r.amount_paid}</TableCell>
                    <TableCell align="right">{r.cash_received}</TableCell>
                    <TableCell align="right">{r.old_cash_balance}</TableCell>
                    <TableCell align="right">{r.commission_pct}</TableCell>
                    <TableCell align="right">{r.adjustments}</TableCell>
                    <TableCell>{r.txn_type}</TableCell>
                    <TableCell>{r.site_contact_person}</TableCell>
                    <TableCell>{r.rectification}</TableCell>
                    <TableCell>{r.purpose}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(r);
                          }}
                          disabled={!canUpdate}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDelete(r);
                          }}
                          disabled={!canDelete}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={viewOpen && Boolean(viewRow)}
        onClose={() => {
          setViewOpen(false);
          setViewRow(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Transaction Details
          {viewRow && (
            <Chip
              size="small"
              label={viewRow.txn_type || 'Txn'}
              sx={{ fontWeight: 700, borderRadius: 999 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(148,163,184,0.16)' : 'rgba(226,232,240,0.6)'),
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Chip
                    size="small"
                    label={String(viewRow.txn_date).slice(0, 10)}
                    sx={{ borderRadius: 2, fontSize: 13, px: 0.5 }}
                  />
                  <Chip
                    size="small"
                    label={viewRow.user_code}
                    sx={{ borderRadius: 2, fontSize: 13, px: 0.5 }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>From Vendor Code</Typography>
                    <Typography sx={{ fontWeight: 750, fontSize: 17 }}>{viewRow.from_vendor_code || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>From Bank Code</Typography>
                    <Typography>{viewRow.from_bank_code || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Site Contact Person</Typography>
                    <Typography>{viewRow.site_contact_person || '-'}</Typography>
                  </Box>
                </Stack>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>To Vendor Code</Typography>
                    <Typography sx={{ fontWeight: 750, fontSize: 17 }}>{viewRow.to_vendor_code || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>To Bank Code</Typography>
                    <Typography>{viewRow.to_bank_code || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Purpose</Typography>
                    <Typography>{viewRow.purpose || '-'}</Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Amount Paid</Typography>
                  <Typography>
                    {Number.isFinite(Number(viewRow.amount_paid))
                      ? Number(viewRow.amount_paid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cash Received</Typography>
                  <Typography>
                    {Number.isFinite(Number(viewRow.cash_received))
                      ? Number(viewRow.cash_received).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Old Cash Balance</Typography>
                  <Typography>
                    {Number.isFinite(Number(viewRow.old_cash_balance))
                      ? Number(viewRow.old_cash_balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Commission %</Typography>
                  <Typography>
                    {Number.isFinite(Number(viewRow.commission_pct))
                      ? Number(viewRow.commission_pct).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Adjustments</Typography>
                  <Typography>
                    {Number.isFinite(Number(viewRow.adjustments))
                      ? Number(viewRow.adjustments).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Rectification</Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{viewRow.rectification || '-'}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setViewOpen(false);
              setViewRow(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Transaction</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="User Code" value={form.userCode} onChange={handleFormChange('userCode')} fullWidth />
              <TextField label="Date" type="date" value={form.date} onChange={handleFormChange('date')} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="From Vendor Code" value={form.fromVendorCode} onChange={handleFormChange('fromVendorCode')} fullWidth />
              <TextField label="From Bank Code" value={form.fromBankCode} onChange={handleFormChange('fromBankCode')} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="To Vendor Code" value={form.toVendorCode} onChange={handleFormChange('toVendorCode')} fullWidth />
              <TextField label="To Bank Code" value={form.toBankCode} onChange={handleFormChange('toBankCode')} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Amount Paid" value={form.amountPaid} onChange={handleFormChange('amountPaid')} fullWidth />
              <TextField label="Cash Received" value={form.cashReceived} onChange={handleFormChange('cashReceived')} fullWidth />
              <TextField label="Old Cash Balance" value={form.oldCashBalance} onChange={handleFormChange('oldCashBalance')} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Commission %" value={form.commissionPct} onChange={handleFormChange('commissionPct')} fullWidth />
              <TextField label="Adjustments" value={form.adjustments} onChange={handleFormChange('adjustments')} fullWidth />
              <TextField label="Txn Type" value={form.txnType} onChange={handleFormChange('txnType')} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Site Contact Person" value={form.siteContactPerson} onChange={handleFormChange('siteContactPerson')} fullWidth />
              <TextField label="Rectification" value={form.rectification} onChange={handleFormChange('rectification')} fullWidth />
              <TextField label="Purpose" value={form.purpose} onChange={handleFormChange('purpose')} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>Are you sure you want to delete this transaction?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <ExportOtpDialog
        open={otpDialogOpen}
        onClose={() => {
          setOtpDialogOpen(false);
          setPendingExportType(null);
        }}
        onSuccess={onOtpSuccess}
      />
    </Layout>
  );
}
